import { useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";

import { createContactsRequestDto } from "app/pages/sales-intelligence/modules/company-analysis/helpers/contacts";
import { useSelectedAvailableCountries } from "app/pages/sales-intelligence/modules/company-analysis/hooks/useSelectedAvailableCountries";
import {
    selectActiveProfile,
    selectContactDetails,
    selectContactsCorrectCurrentPage,
    selectContactsPageSize,
    selectContactsTableSearchText,
    selectContactsTableSortBy,
} from "app/pages/sales-intelligence/modules/company-analysis/store/contacts/selectors";
import useRevealQuotaFetchEffect from "app/pages/sales-intelligence/sub-modules/contacts-search/hooks/useRevealQuotaFetchEffect";
import useContactsSearchWithTotalCountQuery from "app/pages/sales-intelligence/sub-modules/contacts-search/query/queries/useContactsSearchWithTotalCountQuery";
import { contactsSearchResultsWithTotalCountFetchedAsync } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/action-creators";
import {
    selectActionsPanelFilters,
    selectQuotaIsBeingFetched,
} from "app/pages/sales-intelligence/sub-modules/contacts-search/store/selectors";
import { selectIsContactsVisible } from "app/pages/sales-intelligence/sub-modules/contacts-tab/store/selectors";

export default function useContactsFetchEffect(domain: string): () => Promise<unknown> {
    const dispatch = useDispatch();
    // Quota info
    const isQuotaBeingFetched = useSelector(selectQuotaIsBeingFetched);
    // Contacts countries
    const { selectedCountriesIds, isCountriesTransitioning } = useSelectedAvailableCountries();

    // Contacts profiles
    const selectedProfile = useSelector(selectActiveProfile);
    // Contacts details
    const contactDetails = useSelector(selectContactDetails);
    // Contacts search table
    const searchText = useSelector(selectContactsTableSearchText);
    // The export panel resets the page through `actionsPanelFilters.page`; the table shows that corrected page too
    const currentPage = useSelector(selectContactsCorrectCurrentPage);
    const pageSize = useSelector(selectContactsPageSize);
    // Contacts actions panel
    const actionsPanelFilters = useSelector(selectActionsPanelFilters);
    const sortBy = useSelector(selectContactsTableSortBy);

    // Access info
    const isContactsVisible = useSelector(selectIsContactsVisible);

    const requestDTO = createContactsRequestDto({
        domain,
        searchText,
        currentPage,
        pageSize,
        contactDetails,
        selectedProfile,
        selectedCountriesIds,
        actionsPanelFilters,
        sortBy,
    });

    // Quota fetch
    useRevealQuotaFetchEffect();

    const { refetch } = useContactsSearchWithTotalCountQuery(requestDTO, {
        enabled: isContactsVisible && !isQuotaBeingFetched && !isCountriesTransitioning,
    });

    useEffect(() => {
        if (isCountriesTransitioning) {
            dispatch(contactsSearchResultsWithTotalCountFetchedAsync.request());
        }
    }, [isCountriesTransitioning]);

    return refetch;
}
